import { computed, Signal, WritableSignal } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { PageEvent } from "@angular/material/paginator";

import { debounceTime, skip } from "rxjs/operators";

import { PaginatedApiResponse } from "@lib/types";
import { DEFAULT_PAGE_SIZE } from "@lib/constants/pagination";

import {
  BaseQueryParams,
  CreateDefaultPaginatedValueOptions,
  DebouncedSearchConfig,
  PaginationComputedSignals,
  ResourceStateComputedSignals,
  SortDirection,
} from "./list-page.types";

/**
 * Interface representing an rxResource-like object.
 * Used to avoid importing internal Angular types.
 */
interface ResourceLike<T> {
  value: Signal<T | undefined>;
  isLoading: Signal<boolean>;
  status: Signal<
    | "idle"
    | "loading"
    | "reloading"
    | "refreshing"
    | "error"
    | "resolved"
    | "local"
  >;
}

/**
 * Creates a default value for PaginatedApiResponse to use with rxResource.
 * Reduces boilerplate when setting up paginated resource defaults.
 *
 * @param options - Optional configuration for the default value
 * @returns A default PaginatedApiResponse object
 */
export function createDefaultPaginatedValue<T>(
  options?: CreateDefaultPaginatedValueOptions,
): PaginatedApiResponse<T> {
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  return {
    data: [],
    totalCount: 0,
    pageNumber: 0,
    pageSize,
    totalPages: 0,
    success: true,
    errors: [],
    timestamp: "",
    traceId: "",
    message: "",
    hasNextPage: false,
    hasPreviousPage: false,
  };
}

/**
 * Creates default query params for a list page.
 * Can be extended with additional properties.
 *
 * @param overrides - Optional overrides for the default values
 * @returns Default query params object
 */
export function createDefaultQueryParams<T extends BaseQueryParams>(
  overrides?: Partial<T>,
): BaseQueryParams {
  return {
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    searchText: undefined,
    sortBy: undefined,
    sortDirection: undefined,
    ...overrides,
  };
}

/**
 * Creates computed signals for pagination data from a paginated resource.
 *
 * @param resource - The rxResource containing paginated data
 * @returns Object with computed signals for pagination properties
 */
export function createPaginationComputedSignals<T>(
  resource: ResourceLike<PaginatedApiResponse<T>>,
): PaginationComputedSignals<T> {
  return {
    items: computed(() => resource.value()?.data ?? []),
    totalCount: computed(() => resource.value()?.totalCount ?? 0),
    totalPages: computed(() => resource.value()?.totalPages ?? 0),
    hasPreviousPage: computed(() => resource.value()?.hasPreviousPage ?? false),
    hasNextPage: computed(() => resource.value()?.hasNextPage ?? false),
  };
}

/**
 * Creates computed signals for resource loading/error state.
 *
 * @param resource - The rxResource to monitor
 * @returns Object with isLoading and hasError computed signals
 */
export function createResourceStateSignals(
  resource: ResourceLike<unknown>,
): ResourceStateComputedSignals {
  return {
    isLoading: computed(() => resource.isLoading()),
    hasError: computed(() => resource.status() === "error"),
  };
}

/**
 * Creates a page event handler that updates query params.
 *
 * @param queryParams - The query params signal to update
 * @returns A function that handles PageEvent and updates query params
 */
export function createPageEventHandler<T extends BaseQueryParams>(
  queryParams: WritableSignal<T>,
): (event: PageEvent) => void {
  return (event: PageEvent) => {
    queryParams.update((params) => ({
      ...params,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
    }));
  };
}

/**
 * Creates a sort change handler that toggles sort direction.
 *
 * @param queryParams - The query params signal to update
 * @returns A function that handles sort column changes
 */
export function createSortChangeHandler<T extends BaseQueryParams>(
  queryParams: WritableSignal<T>,
): (sortBy: string) => void {
  return (sortBy: string) => {
    queryParams.update((params) => {
      const isSameColumn = params.sortBy === sortBy;
      const newDirection: SortDirection =
        isSameColumn && params.sortDirection === "asc" ? "desc" : "asc";

      return {
        ...params,
        sortBy,
        sortDirection: newDirection,
      };
    });
  };
}

/**
 * Creates an update search function that updates query params.
 *
 * @param queryParams - The query params signal to update
 * @returns A function that updates search text in query params
 */
export function createUpdateSearchHandler<T extends BaseQueryParams>(
  queryParams: WritableSignal<T>,
): (searchText: string) => void {
  return (searchText: string) => {
    queryParams.update((params) => ({
      ...params,
      searchText: searchText.trim() || undefined,
      pageIndex: 0,
    }));
  };
}

/**
 * Sets up a debounced search subscription.
 * Must be called from a constructor or injection context for takeUntilDestroyed to work.
 *
 * Note: Uses skip(1) to avoid triggering an unnecessary API call on initialization.
 * The rxResource will already make the initial call with the default queryParams.
 *
 * @param config - Configuration for the debounced search
 */
export function setupDebouncedSearch<T extends BaseQueryParams>(
  config: DebouncedSearchConfig<T>,
): void {
  const { searchInput, queryParams, debounceMs = 1000 } = config;
  const updateSearch = createUpdateSearchHandler(queryParams);

  toObservable(searchInput)
    .pipe(skip(1), debounceTime(debounceMs), takeUntilDestroyed())
    .subscribe((searchText) => updateSearch(searchText));
}
